import { rememberCurrentLocation } from '../utils/postLoginRedirect';

export const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const TIMEOUT_MS = 10_000;

export class ApiError extends Error {
    response: { status: number; data: unknown };
    constructor(status: number, data: unknown) {
        super(`HTTP ${status}`);
        this.name = 'ApiError';
        this.response = { status, data };
    }
}

export function isApiError(e: unknown): e is ApiError {
    return e instanceof ApiError;
}

interface Opts {
    params?: Record<string, string | number | undefined>;
    headers?: Record<string, string>;
    // Overrides TIMEOUT_MS for calls known to take longer than a typical
    // request (e.g. cloning a large starter collection synchronously inserts
    // thousands of rows server-side — see collectionService.cloneStarterCollection).
    timeoutMs?: number;
    // Lets a caller cancel this specific request independently of the internal timeout —
    // e.g. aborting an in-flight search the moment a newer one supersedes it, so an abandoned
    // request doesn't keep running against the backend/external API just to have its result
    // discarded on arrival.
    signal?: AbortSignal;
    // A public, anonymous GET: no auth/platform/content-type headers and no cookies, so the
    // browser sends it as a simple cross-origin request without a CORS preflight (see
    // apiClient.getPublic). Keeps the timeout and ApiError handling.
    anonymous?: boolean;
}

function getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
}

function buildUrl(path: string, params?: Record<string, string | number | undefined>): string {
    const url = BASE_URL + path;
    if (!params) return url;
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) {
        if (v !== undefined && v !== '') qs.set(k, String(v));
    }
    const str = qs.toString();
    return str ? `${url}?${str}` : url;
}

function buildHeaders(body: unknown, extraHeaders?: Record<string, string>): Record<string, string> {
    const isFormData = body instanceof FormData;
    const headers: Record<string, string> = {
        ...getAuthHeaders(),
        // Feature-flag platform targeting (see weavewords-api feature_flags_handler.go).
        'X-Platform': 'web',
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    };
    if (extraHeaders) {
        for (const [k, v] of Object.entries(extraHeaders)) {
            // Don't let callers override Content-Type for FormData — browser must set it (includes boundary)
            if (isFormData && k.toLowerCase() === 'content-type') continue;
            headers[k] = v;
        }
    }
    return headers;
}

async function parseBody(res: Response): Promise<unknown> {
    const text = await res.text();
    if (!text) return null;
    try { return JSON.parse(text); } catch { return text; }
}

async function send<T>(
    method: string,
    path: string,
    body?: unknown,
    opts?: Opts,
): Promise<{ data: T }> {
    const url = buildUrl(path, opts?.params);
    const headers = opts?.anonymous ? {} : buildHeaders(body, opts?.headers);
    const bodyInit = body instanceof FormData
        ? body
        : body !== undefined ? JSON.stringify(body) : undefined;

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), opts?.timeoutMs ?? TIMEOUT_MS);
    const onExternalAbort = () => controller.abort();
    opts?.signal?.addEventListener('abort', onExternalAbort);

    let res: Response;
    try {
        res = await fetch(url, {
            method,
            headers,
            body: bodyInit,
            credentials: opts?.anonymous ? 'omit' : 'include',
            signal: controller.signal,
        });
    } catch (err) {
        clearTimeout(timer);
        opts?.signal?.removeEventListener('abort', onExternalAbort);
        throw err;
    }
    clearTimeout(timer);
    opts?.signal?.removeEventListener('abort', onExternalAbort);

    // 401 = the session is gone (an anonymous request has none to lose). There is no refresh
    // token: JWTs live 30 days and the user signs in again. The API has no /auth/refresh route.
    if (res.status === 401 && !opts?.anonymous) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken'); // legacy key, never written any more
        localStorage.removeItem('userAuth');
        // Guard against a redirect storm: several protected requests can all
        // 401 at once (e.g. on dashboard mount), each independently reaching
        // this branch. Only the first should force navigation.
        if (window.location.pathname !== '/login') {
            // An expired session on the AI-app consent page must come back to it after sign-in.
            rememberCurrentLocation();
            window.location.href = '/login';
        }
        throw new ApiError(401, {});
    }

    if (!res.ok) {
        throw new ApiError(res.status, await parseBody(res));
    }

    return { data: (await parseBody(res)) as T };
}

const apiClient = {
    get:    <T = any>(path: string, opts?: Opts) =>
                send<T>('GET', path, undefined, opts),
    // For public endpoints anonymous visitors hit (share pages): see Opts.anonymous.
    getPublic: <T = any>(path: string, opts?: Omit<Opts, 'anonymous' | 'headers'>) =>
                send<T>('GET', path, undefined, { ...opts, anonymous: true }),
    post:   <T = any>(path: string, body?: unknown, opts?: Opts) =>
                send<T>('POST', path, body, opts),
    put:    <T = any>(path: string, body?: unknown, opts?: Opts) =>
                send<T>('PUT', path, body, opts),
    delete: <T = any>(path: string, body?: unknown, opts?: Opts) =>
                send<T>('DELETE', path, body, opts),
};

export default apiClient;
